/**
 * Register-verb authority (13-22): who may run which register verb on which record, and what is waiting on whom.
 * Pure functions, no I/O, no React.
 *
 *   - `canDoRecordVerb(verb, row, actor, ctx)`: the one gate every record panel and the record writer call
 *     (D-13-L-1, D-13-L-2, D-13-L-17, D-13-L-19);
 *   - `recordsWaitingOn(actor, rows, now, ctx)`: the lens 13-29 renders on Today (with what is due on each item), and
 *     the record page's "this is yours";
 *   - `isMyStream(actor, row)`: the "your stream" marker (D-13-L-15). Shown, never a gate.
 *
 * A separate model from `ROLE_REGISTER_PERMISSIONS` in `config/roles.ts`, whose `lodge` / `view` / `investigate`
 * floor stays untouched (D-13-L-1). `lodge` alone is answered from that floor, so the two never disagree.
 *
 * The classifications are this module's own: whether a record is aged care is read from its `BusinessUnit`, and
 * which regulator a notice is for is raised by the columns being written. No caller flag can lower either, so a
 * regulatory verb never opens to a non-decider because a caller passed the wrong flag (the D-13-05-2 rule).
 * Reportability keys on `BusinessUnit` and `ReportabilityDecision` only; the frozen reporting column is never read
 * (D-12.1-04-C).
 *
 * State and column refusals are 13-20's / 13-21's builders' job; this module answers only "may this person do it".
 */

import {
  AGED_CARE_STREAMS,
  SAH_DECIDER_DELEGATES,
  SAH_DECIDER_ROLE,
  SAH_REPORTABILITY_DECIDERS,
  STREAM_CARE_MANAGERS,
  WORKSAFE_NOTICE_DELEGATES,
  WORKSAFE_NOTIFIER_ROLE,
  type DelegateEntry,
} from '../config/fieldset/deciders';
import { liveChoice } from '../config/fieldset/live';
import { CLIENT_STREAMS, RECORD_REGISTERS, VERB_ORDER, verbColumns, verbsOf } from '../config/fieldset/registerFields';
import { canDo } from '../config/roles';
import { OFFICE_ROLES, type AppRole } from '../types/auth';
import type { Actor } from '../types/actionRegister';
import type { RecordRegister, RecordRow, RecordVerbKey } from '../types/records';
import { isOversight, sameUpn } from './actionAuthority';
import { melbourneDayOf, todayMelbourne } from './fieldValues';
import { DEADLINE_RULES, noticeDeadlines, whsDayDeadlines, type RuleKey } from './notificationDeadlines';
import { columnsToComplete } from './reportCompleteness';

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/** Every refusal, as stable codes. `RECORD_AUTHORITY_TEXT` gives each one in plain words for the UI. */
export const RECORD_AUTHORITY_REASONS = [
  /** Not a register verb (a runtime string). Refused, never allowed. */
  'unknown-verb',
  /** The row's register is not one of the five. */
  'unknown-register',
  /** The register has no such verb (e.g. `close` on an Incident, which closes in two steps, D-13-L-3). */
  'verb-not-on-register',
  /** The actor holds no register App Role (Oversight / CareManager / HRWHS), and is not a decider. */
  'no-role',
  /** Oversight only. */
  'oversight-only',
  /** Any CareManager, or Oversight. */
  'care-manager-or-oversight',
  /** Reportability or a regulator notice, and the actor is neither Oversight nor (for aged care) the decider. */
  'not-a-decider',
  /** Aged-care reportability or a SIRS notice: the decider(s) only. The actor is Oversight but not a decider. */
  'sah-decider-only',
  /** The WHS verb or Risk `HazardType`: HR & WHS only. */
  'hrwhs-only',
  /** The WorkSafe notice: HR & WHS, or a named WorkSafe-notice delegate (D-13-L-2, D-13-33). */
  'worksafe-notifier-only',
  /** The notification verb, but which notice it records is unknown (fail closed). */
  'notice-unknown',
  /** Approve and close by the person who ran ready for approval (no self-approval, D-13-L-19). */
  'self-approval',
  /** Approve and close while the preparer is unknown: no proof either way, so refused (D-13-L-19). */
  'preparer-unknown',
] as const;

export type RecordAuthorityReason = (typeof RECORD_AUTHORITY_REASONS)[number];

export type RecordAuthorityResult = { ok: true } | { ok: false; reason: RecordAuthorityReason };

/** ` (or, while they are away, A, then B)`, from a delegate list in its order; `''` when there is none. */
function awayClause(delegates: readonly DelegateEntry[]): string {
  return delegates.length === 0 ? '' : ` (or, while they are away, ${delegates.map((d) => d.role).join(', then ')})`;
}

/**
 * The delegates sentence the panels' "who decides" lines end with (D-13-L-2, D-13-33): "If they are away: the system
 * owner, then the People & Culture lead." Roles only, in the list's order; `''` when the list is empty.
 */
export function delegatesLine(delegates: readonly DelegateEntry[]): string {
  return delegates.length === 0 ? '' : `If they are away: ${delegates.map((d) => d.role).join(', then ')}.`;
}

/** The SaH decider's delegates sentence ("If they are away: …"), for the reportability and SIRS lines. */
export const SAH_DELEGATES_LINE = delegatesLine(SAH_DECIDER_DELEGATES);

/** The WorkSafe notice's delegates sentence, for the WorkSafe lines. */
export const WORKSAFE_DELEGATES_LINE = delegatesLine(WORKSAFE_NOTICE_DELEGATES);

/** Each refusal in plain words, for the record page and the panels. */
export const RECORD_AUTHORITY_TEXT: Readonly<Record<RecordAuthorityReason, string>> = {
  'unknown-verb': 'This is not a register step the dashboard knows.',
  'unknown-register': 'This record is not on one of the five registers.',
  'verb-not-on-register': 'This register does not have that step.',
  'no-role': 'Your account has no QMS role that can do this.',
  'oversight-only': 'Only Oversight can do this.',
  'care-manager-or-oversight': 'Only a Care Manager or Oversight can do this.',
  'not-a-decider': 'Reportability and regulator notices are recorded by Oversight, or for aged care by the SaH decider.',
  'sah-decider-only': `For aged care, only ${SAH_DECIDER_ROLE}${awayClause(SAH_DECIDER_DELEGATES)} can record this.`,
  'hrwhs-only': 'Only HR & WHS can do this.',
  'worksafe-notifier-only': `Only ${WORKSAFE_NOTIFIER_ROLE}${awayClause(WORKSAFE_NOTICE_DELEGATES)} can record the WorkSafe notice.`,
  'notice-unknown': 'Choose which notice this is (NDIS, SIRS or WorkSafe) first.',
  'self-approval': 'Someone else must approve this, because you prepared it for approval.',
  'preparer-unknown': "We can't tell who prepared this for approval, so it can't be approved yet. Check its trail.",
};

// ---------------------------------------------------------------------------
// Notices (D-13-L-2: NDIS → Oversight, SIRS → the SaH deciders, WorkSafe → HR & WHS)
// ---------------------------------------------------------------------------

export type NoticeKind = 'ndis' | 'sirs' | 'worksafe';

export const NOTICE_KINDS: readonly NoticeKind[] = ['ndis', 'sirs', 'worksafe'];

const NOTICE_PREFIX: Readonly<Record<NoticeKind, string>> = { ndis: 'Ndis', sirs: 'Sirs', worksafe: 'WorkSafe' };

/**
 * The notification verb's columns that belong to one regulator's notice, by their FIELDSET name prefix, in the
 * verb's order. The shared columns (notifier, outcome, regulator reference, final report) belong to none.
 */
function noticeColumnsOf(kind: NoticeKind): readonly string[] {
  return verbColumns('incidents', 'notification').filter((col) => col.startsWith(NOTICE_PREFIX[kind]));
}

export const NOTICE_COLUMNS: Readonly<Record<NoticeKind, readonly string[]>> = {
  ndis: noticeColumnsOf('ndis'),
  sirs: noticeColumnsOf('sirs'),
  worksafe: noticeColumnsOf('worksafe'),
};

const NOTICE_OF_COLUMN: ReadonlyMap<string, NoticeKind> = new Map(
  NOTICE_KINDS.flatMap((kind) => NOTICE_COLUMNS[kind].map((col) => [col, kind] as const)),
);

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface RecordAuthorityContext {
  /**
   * `approveAndClose`: the UPN of whoever ran *ready for approval* (the latest `Status → Evaluating` sibling row's
   * `ActorUPN`, D-13-L-19), resolved by the caller. Absent or blank → refused `preparer-unknown`.
   */
  preparedByUpn?: string;
  /** `notification`: the notice being recorded. */
  notice?: NoticeKind;
  /**
   * `notification`: the columns the save writes. They can only RAISE the notice: a `Sirs…` column makes it a SIRS
   * notice whatever `notice` says, and every notice the save touches must be permitted.
   */
  columns?: readonly string[];
}

// ---------------------------------------------------------------------------
// Small predicates
// ---------------------------------------------------------------------------

const REGISTER_ROLES: readonly AppRole[] = ['Oversight', 'CareManager', 'HRWHS'];
const CLIENT_FACING: ReadonlySet<RecordRegister> = new Set<RecordRegister>(['incidents', 'complaints', 'feedback']);
const KNOWN_REGISTERS: ReadonlySet<string> = new Set<string>(RECORD_REGISTERS);

function rolesOf(actor: Actor): readonly AppRole[] {
  return Array.isArray(actor.roles) ? actor.roles : [];
}

function holds(actor: Actor, role: AppRole): boolean {
  return rolesOf(actor).includes(role);
}

function upnOf(actor: Actor): string {
  return typeof actor.upn === 'string' ? actor.upn : '';
}

function isBlank(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function listed(actor: Actor, upns: readonly string[]): boolean {
  const upn = upnOf(actor);
  return upn.trim() !== '' && upns.some((d) => sameUpn(d, upn));
}

/**
 * The actor is the SaH decider or one of the decider's delegates (`SAH_REPORTABILITY_DECIDERS`,
 * `SAH_DECIDER_DELEGATES`; case and surrounding spaces ignored, D-13-05-5). Any listed person may act (D-13-33).
 */
export function isSahDecider(actor: Actor): boolean {
  return listed(actor, SAH_REPORTABILITY_DECIDERS) || listed(actor, SAH_DECIDER_DELEGATES.map((d) => d.upn));
}

/** The actor is a named WorkSafe-notice delegate (`WORKSAFE_NOTICE_DELEGATES`, D-13-33). Not the App Role. */
export function isWorkSafeDelegate(actor: Actor): boolean {
  return listed(actor, WORKSAFE_NOTICE_DELEGATES.map((d) => d.upn));
}

/** The actor may record the WorkSafe notice: the HR & WHS App Role, or a named delegate (D-13-L-2, D-13-33). */
export function isWorkSafeNotifier(actor: Actor): boolean {
  return holds(actor, 'HRWHS') || isWorkSafeDelegate(actor);
}

/**
 * The actor holds a register App Role (Oversight, CareManager, HRWHS) or is a decider: who may read a panel they
 * can't act on (13-27: the reportability and notice panels, read only, so a pending call is never hidden). Finance
 * alone sees no panel (13-25). No behaviour of `canDoRecordVerb` changes.
 */
export function holdsRegisterRole(actor: Actor): boolean {
  return REGISTER_ROLES.some((role) => holds(actor, role)) || isSahDecider(actor) || isWorkSafeDelegate(actor);
}

/**
 * The record's `BusinessUnit` is one the SaH decider decides (D-13-L-2): aged care, and since D-13-22-4's ruling
 * (13 Oct 2026) both mixed units. A blank or unknown unit is not.
 */
export function isAgedCareRecord(row: RecordRow): boolean {
  return AGED_CARE_STREAMS.includes(textOf(row.fields.BusinessUnit));
}

// ---------------------------------------------------------------------------
// The rules (D-13-L-2)
// ---------------------------------------------------------------------------

type Rule = (row: RecordRow, actor: Actor, ctx: RecordAuthorityContext) => RecordAuthorityResult;

const OK: RecordAuthorityResult = { ok: true };
const refuse = (reason: RecordAuthorityReason): RecordAuthorityResult => ({ ok: false, reason });

const oversightOnly: Rule = (_row, actor) => (isOversight(actor) ? OK : refuse('oversight-only'));

const careManagerOrOversight: Rule = (_row, actor) =>
  isOversight(actor) || holds(actor, 'CareManager') ? OK : refuse('care-manager-or-oversight');

const hrwhsOnly: Rule = (_row, actor) => (holds(actor, 'HRWHS') ? OK : refuse('hrwhs-only'));

/** triage / handling / close / cancel: CareManager or Oversight on Incidents, Complaints, Feedback; else Oversight. */
const byRegister: Rule = (row, actor, ctx) =>
  CLIENT_FACING.has(row.register) ? careManagerOrOversight(row, actor, ctx) : oversightOnly(row, actor, ctx);

/** The SaH decider(s) by UPN; Oversight otherwise hears `sah-decider-only`, anyone else `not-a-decider`. */
function deciderOnly(actor: Actor): RecordAuthorityResult {
  if (isSahDecider(actor)) return OK;
  return isOversight(actor) ? refuse('sah-decider-only') : refuse('not-a-decider');
}

/** Reportability: aged care → the decider(s); every other unit, blank included → any Oversight member. */
const reportabilityRule: Rule = (row, actor) => {
  if (isAgedCareRecord(row)) return deciderOnly(actor);
  return isOversight(actor) ? OK : refuse('not-a-decider');
};

const NOTICE_RULES: Readonly<Record<NoticeKind, (actor: Actor) => RecordAuthorityResult>> = {
  ndis: (actor) => (isOversight(actor) ? OK : refuse('oversight-only')),
  sirs: deciderOnly,
  worksafe: (actor) => (isWorkSafeNotifier(actor) ? OK : refuse('worksafe-notifier-only')),
};

/** The notices a notification save records: `ctx.notice` plus every notice its columns touch; `null` = unknown. */
function noticesOf(ctx: RecordAuthorityContext): ReadonlySet<NoticeKind> | null {
  const kinds = new Set<NoticeKind>();
  if (ctx.notice !== undefined) {
    if (!NOTICE_KINDS.includes(ctx.notice)) return null;
    kinds.add(ctx.notice);
  }
  if (Array.isArray(ctx.columns)) {
    for (const col of ctx.columns) {
      const kind = NOTICE_OF_COLUMN.get(col);
      if (kind) kinds.add(kind);
    }
  }
  return kinds.size > 0 ? kinds : null;
}

/** Notification: every notice the save touches must be the actor's (checked in NDIS, SIRS, WorkSafe order). */
const notificationRule: Rule = (_row, actor, ctx) => {
  const kinds = noticesOf(ctx);
  if (kinds === null) return refuse('notice-unknown');
  for (const kind of NOTICE_KINDS) {
    if (!kinds.has(kind)) continue;
    const result = NOTICE_RULES[kind](actor);
    if (!result.ok) return result;
  }
  return OK;
};

/**
 * No self-approval (D-13-L-19): the preparer must be known and must not be the approver. Exported so 13-21's
 * approve-and-close builder refuses the same way without a second copy of the rule.
 */
export function checkPreparer(preparedByUpn: unknown, approverUpn: string): RecordAuthorityResult {
  const preparer = typeof preparedByUpn === 'string' ? preparedByUpn : '';
  if (preparer.trim() === '') return refuse('preparer-unknown');
  return sameUpn(preparer, approverUpn) ? refuse('self-approval') : OK;
}

/** Approve and close (Incidents): Oversight, never the preparer, and the preparer must be known (D-13-L-19). */
const approveAndCloseRule: Rule = (_row, actor, ctx) => {
  if (!isOversight(actor)) return refuse('oversight-only');
  return checkPreparer(ctx.preparedByUpn, upnOf(actor));
};

/** Lodge: the `config/roles.ts` floor, office roles only (field reporters lodge through FieldLink). */
const lodgeRule: Rule = (row, actor) => {
  const office = rolesOf(actor).filter((role) => OFFICE_ROLES.includes(role));
  return canDo([...office], row.register, 'lodge') ? OK : refuse('no-role');
};

/**
 * The permission map. A `Record` over `RecordVerbKey`, so a new verb does not compile until its rule is set here.
 * Risk `HazardType` is the `whs` verb on a Risk (13-17's `verbColumns('risk', 'whs')`), so `hrwhsOnly` covers it.
 */
const VERB_RULES: Readonly<Record<RecordVerbKey, Rule>> = {
  lodge: lodgeRule,
  // 13.2 (D-13.2-P-3): any Care Manager, or Oversight, completes an incident's report (an Incidents-only verb).
  complete: careManagerOrOversight,
  triage: byRegister,
  reportability: reportabilityRule,
  whs: hrwhsOnly,
  notification: notificationRule,
  handling: byRegister,
  readyForApproval: careManagerOrOversight,
  approveAndClose: approveAndCloseRule,
  // ISS-259 (D-13-33): Oversight returns a readied incident. Not an approval, so the preparer rule does not apply.
  returnForRework: oversightOnly,
  close: byRegister,
  cancel: byRegister,
  // 13.2 (D-13.2-P-10): only Oversight closes an incident as a duplicate of another.
  closeAsDuplicate: oversightOnly,
  reopen: oversightOnly,
  ciApprove: oversightOnly,
  riskReview: oversightOnly,
  riskRetire: oversightOnly,
};

/** The verbs a decider may run without a register App Role (the constant is the authority, D-13-L-2). */
const DECIDER_VERBS: ReadonlySet<RecordVerbKey> = new Set<RecordVerbKey>(['reportability', 'notification']);

/** May this actor pass the no-role check for this verb by being listed (a decider, a delegate)? */
function listedFor(verb: RecordVerbKey, actor: Actor): boolean {
  if (!DECIDER_VERBS.has(verb)) return false;
  // A WorkSafe-notice delegate passes only for the notification verb; its rule still allows the WorkSafe notice only.
  return isSahDecider(actor) || (verb === 'notification' && isWorkSafeDelegate(actor));
}

const KNOWN_VERBS: ReadonlySet<string> = new Set<string>(VERB_ORDER);

/**
 * May `actor` run `verb` on `row`? Order: unknown verb → unknown register → a verb the register lacks → no
 * register role (lodge and the decider verbs excepted) → the verb's rule. `ctx` carries only facts the caller
 * resolved (the preparer, the notice and its columns); no flag in it can widen the answer.
 */
export function canDoRecordVerb(
  verb: RecordVerbKey,
  row: RecordRow,
  actor: Actor,
  ctx: RecordAuthorityContext = {},
): RecordAuthorityResult {
  if (!KNOWN_VERBS.has(verb) || !Object.prototype.hasOwnProperty.call(VERB_RULES, verb)) {
    return refuse('unknown-verb');
  }
  if (!KNOWN_REGISTERS.has(row.register)) return refuse('unknown-register');
  if (!verbsOf(row.register).includes(verb)) return refuse('verb-not-on-register');
  const context = ctx ?? {};
  if (verb !== 'lodge' && !REGISTER_ROLES.some((role) => holds(actor, role))) {
    if (!listedFor(verb, actor)) return refuse('no-role');
  }
  return VERB_RULES[verb](row, actor, context);
}

// ---------------------------------------------------------------------------
// recordsWaitingOn: what is waiting on whom
// ---------------------------------------------------------------------------

/** Why a record is waiting on the actor, in display order. */
export const WAITING_KINDS = [
  /**
   * 13.2 (D-13.2-P-3, -4): CareManager: a New or Actioning Incident whose report lacks a field that triage can't add
   * itself (`columnsToComplete`: `missingLodgeColumns` less the triage verb's own columns, so it is non-empty exactly
   * when triage's `report-incomplete` gate refuses every save, and the two never disagree). An intake stub is the
   * usual case. While it is offered, the kinds whose verb is triage are not (triage would refuse them).
   */
  'to-complete',
  /** CareManager: a New Incident / Complaint / Feedback. Oversight: a New Risk / CI. */
  'to-triage',
  /**
   * CareManager: open, and its due day is today or past: Incidents `ResolveBy`, Complaints `TargetResolutionOn`,
   * Feedback 30 days after `ReceivedOn` (C4's ageing; Feedback has no due column, 13-29).
   */
  'handling-due',
  /** CareManager: a client-stream record with a name and no client id (D-13-L-7), where triage can write the id. */
  'client-id-to-add',
  /** CareManager (13-29): an Evaluating Complaint or Feedback, which a CareManager closes in one step (D-13-L-3). */
  'to-close',
  /** Oversight: an Evaluating Incident prepared by someone else. */
  'to-approve',
  /** Oversight: an Evaluating Incident whose preparer is unknown. Shown so it is never hidden; not approvable. */
  'preparer-unknown',
  /**
   * Oversight (and the decider): reportability Pending or blank, where this actor may decide it. 13.2 (D-13.2-P-5):
   * it carries the CM's draft view, and a provisional initial-notice due from a reportable draft and `AwareAt`.
   */
  'reportability-pending',
  /**
   * 13-29: a regulator notice the decision calls for and nobody has recorded yet, for whoever records that notice
   * (D-13-L-2: NDIS any Oversight member, SIRS the SaH decider, WorkSafe HR & WHS), with each notice's due time from
   * `notificationDeadlines` (the initial notice, its follow-up, the WorkSafe written record, a required final report).
   */
  'notice-due',
  /**
   * HR & WHS: an Incident with a worker injured or a WorkSafe decision, and `WorkSafeNotifiable` blank. 13.2
   * (D-13.2-P-6): also when the CM asks HR & WHS in (`HrWhsTriageNeeded = Yes`) or drafts it as WorkSafe.
   */
  'whs-to-triage',
  /**
   * HR & WHS (13-29, ISS-270): a WHS "by" day (`whsDayDeadlines`: the claim forward, the medical certificate forward,
   * the mental injury notice, RTW planning) that is due today or has passed, with its done column blank.
   */
  'whs-day-due',
  /** HR & WHS: an open WHS Risk whose `HazardType` is not recorded yet (the WHS verb's one column on a Risk, 13-29). */
  'hazard-open',
] as const;

export type WaitingKind = (typeof WAITING_KINDS)[number];

/**
 * The verb each kind asks for. Every item of a kind with a verb passes `canDoRecordVerb` for it (the queue equals
 * the permission, D-13-05-3); a `notice-due` item passes it for each notice it carries. `preparer-unknown` asks for
 * none: it is an approval that cannot be given yet.
 */
export const VERB_OF_WAITING: Readonly<Record<WaitingKind, RecordVerbKey | null>> = {
  'to-complete': 'complete',
  'to-triage': 'triage',
  'handling-due': 'handling',
  'client-id-to-add': 'triage',
  'to-close': 'close',
  'to-approve': 'approveAndClose',
  'preparer-unknown': null,
  'reportability-pending': 'reportability',
  'notice-due': 'notification',
  'whs-to-triage': 'whs',
  'whs-day-due': 'whs',
  'hazard-open': 'whs',
};

/**
 * One thing due on a waiting record (13-29): what, by when, and whether it has passed. Words only, never a name: the
 * label is a duty ("NDIS 24-hour notice due"), the record's own title is the caller's to show.
 */
export interface WaitingDue {
  /** The duty, worded to sit before its date: "Resolve by", "NDIS 24-hour notice due", "Forward the claim by". */
  label: string;
  /**
   * A Melbourne day `YYYY-MM-DD` (`precision: 'day'`), a UTC instant `…Z` (`'instant'`), or `null` when the clock
   * can't start (a reportable decision with no `AwareAt` and no stored deadline): still shown, never hidden.
   */
  at: string | null;
  precision: 'day' | 'instant';
  /** Past at `now` (a day: before today in Melbourne). Shown amber, never red (D-13-15-4). */
  past: boolean;
  /** The column the duty is recorded in (the notice or done column), or the column the due is read from. */
  column: string;
  /** `notice-due`: the notice the column belongs to, as the notification gate takes it (`ctx.notice`). */
  notice?: NoticeKind;
  /** The `12.1-OBLIGATIONS.md` row the period comes from, when it comes from a rule. */
  obRow?: string | null;
  /**
   * 13.2 (D-13.2-P-5): computed from the CM's draft, not a decision. Display only: it is never written, never sets
   * `NotificationDueAt`, and its label says so ("If confirmed: …").
   */
  provisional?: true;
}

/** The CM's draft reportability view (D-13.2-P-5): `ReportabilityDraft`, and `ReportabilityDraftNote` or `null`. */
export interface ReportabilityDraftView {
  value: string;
  note: string | null;
}

export interface WaitingItem {
  kind: WaitingKind;
  row: RecordRow;
  /** What is due and when (13-29), for the kinds that have a due: handling, reportability, notices, WHS days. */
  dues?: readonly WaitingDue[];
  /** `reportability-pending` only (13.2): the CM's draft view, when `ReportabilityDraft` is set. */
  draft?: ReportabilityDraftView;
}

export interface WaitingContext {
  /** Each Evaluating Incident's preparer (`preparedByUpn`, D-13-L-19), keyed by `RecordRow.ref`, when resolved. */
  preparedByUpn?: Readonly<Record<string, string | undefined>>;
}

const [NEW, ACTIONING, EVALUATING, COMPLETED, CANCELLED] = liveChoice('incidents', 'Status', 'New', 'Actioning', 'Evaluating', 'Completed', 'Cancelled');
/** `Status` is the same five values on every register (each is checked live). */
for (const register of RECORD_REGISTERS) liveChoice(register, 'Status', NEW, ACTIONING, EVALUATING, COMPLETED, CANCELLED);

const [PENDING, WORKSAFE] = liveChoice('incidents', 'ReportabilityDecision', 'Pending', 'WorkSafe');
const [SIRS_P1, SIRS_P2, NDIS_REPORTABLE] = liveChoice('incidents', 'ReportabilityDecision', 'SIRS-P1', 'SIRS-P2', 'NDIS-Reportable');
const [YES] = liveChoice('incidents', 'WorkerInjured', 'Yes');
const [HRWHS_ASKED] = liveChoice('incidents', 'HrWhsTriageNeeded', 'Yes');
/**
 * 13.2 (D-13.2-P-5): the draft values that name a regulator, so a provisional due can be read from them. `Other`
 * names none, and `Unsure` and `NotReportable` start no clock. Each is the decision's own value (checked live on both
 * columns), so the one deadline engine reads the draft as it would the decision.
 */
const DRAFT_WITH_CLOCK: readonly string[] = liveChoice('incidents', 'ReportabilityDraft', 'SIRS-P1', 'SIRS-P2', 'NDIS-Reportable', 'WorkSafe');
for (const value of DRAFT_WITH_CLOCK) liveChoice('incidents', 'ReportabilityDecision', value);
const [DRAFT_WORKSAFE] = liveChoice('incidents', 'ReportabilityDraft', 'WorkSafe');
const [FINAL_REPORT_YES] = liveChoice('incidents', 'FinalReportRequired', 'Yes');
const [WHS_RISK] = liveChoice('risk', 'RiskCategory', 'WHS');

/** The column whose day makes an open record's handling due, and how the due line reads it. */
const DUE_COLUMN: Readonly<Partial<Record<RecordRegister, { column: string; label: string; days?: number }>>> = {
  incidents: { column: 'ResolveBy', label: 'Resolve by' },
  complaints: { column: 'TargetResolutionOn', label: 'Target resolution by' },
  // 13-29: Feedback has no due column by design; C4 ages it 30 days on `ReceivedOn` (12.1-HANDOFF), so the lens does.
  feedback: { column: 'ReceivedOn', label: '30 days from receipt on', days: 30 },
};

/** The registers whose triage verb can set `RecipientClientId` (today Incidents only). */
const CLIENT_ID_REGISTERS: ReadonlySet<RecordRegister> = new Set(
  RECORD_REGISTERS.filter((r) => verbColumns(r, 'triage').includes('RecipientClientId')),
);

/**
 * The record names a client but has no client id yet, and its triage verb can set one (D-13-L-7: "Client id to
 * add" until triage sets it). Exported for the record page's chip (13-25); the CareManager lens uses the same test.
 */
export function needsClientId(row: RecordRow): boolean {
  return (
    CLIENT_ID_REGISTERS.has(row.register) &&
    CLIENT_STREAMS.includes(textOf(row.fields.BusinessUnit)) &&
    !isBlank(row.fields.RecipientName) &&
    isBlank(row.fields.RecipientClientId)
  );
}

// ---------------------------------------------------------------------------
// The dues (13-29): read from the record, computed by `notificationDeadlines` (never a second deadline engine)
// ---------------------------------------------------------------------------

/** How each rule's due line reads (the duty, worded to sit before its date). */
const RULE_LABEL: Readonly<Partial<Record<RuleKey, string>>> = {
  'ndis-24h': 'NDIS 24-hour notice due',
  'ndis-24h-further': 'NDIS 5-day follow-up due',
  'ndis-5bd': 'NDIS 5-business-day notice due',
  'sirs-p1': 'SIRS Priority 1 notice due',
  'sirs-p2': 'SIRS Priority 2 notice due',
  worksafe: 'WorkSafe notice due',
  'worksafe-written-record': 'WorkSafe written record due',
  'sirs-final-report': 'Final report due',
  'ndis-final-report': 'Final report due',
  'whs-claim-forward': 'Forward the claim by',
  'whs-medical-certificate-forward': 'Forward the medical certificate by',
  'whs-mental-injury-notify': 'Notify the mental injury claim by',
  'whs-rtw-planning': 'Start RTW planning by',
};

/** The initial notice of each regime (the one a record with no `AwareAt` still owes). */
const INITIAL_NOTICE_RULES: ReadonlySet<RuleKey> = new Set<RuleKey>(['ndis-24h', 'ndis-5bd', 'sirs-p1', 'sirs-p2', 'worksafe']);

/** An instant the rule selection is probed with when `AwareAt` is missing (the due times are not used). */
const PROBE_AWARE_AT = '2000-01-01T00:00:00Z';

function str(row: RecordRow, column: string): string | null {
  const v = row.fields[column];
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

function instantPast(at: string | null, now: Date): boolean {
  if (at === null) return false;
  const ms = Date.parse(at);
  return Number.isFinite(ms) && ms < now.getTime();
}

function addDaysTo(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The regulator notice a decision's final report belongs to. */
function finalReportNotice(decision: string): { notice: NoticeKind; key: RuleKey } | null {
  if (decision === SIRS_P1 || decision === SIRS_P2) return { notice: 'sirs', key: 'sirs-final-report' };
  if (decision === NDIS_REPORTABLE) return { notice: 'ndis', key: 'ndis-final-report' };
  return null;
}

const OB_ROW_OF: ReadonlyMap<RuleKey, string | null> = new Map(DEADLINE_RULES.map((r) => [r.key, r.obRow]));

/**
 * The notices this record still owes, for this actor: each rule `noticeDeadlines` gives for the stored decision,
 * `AwareAt`, class and WorkSafe answer whose notice column is blank (a conditional notice is never owed), plus a
 * required final report not yet given. Each is kept only when the actor may record that notice (the notification
 * gate with `ctx.notice` and the column), so the lens never offers what the gate refuses.
 */
function noticeDues(row: RecordRow, actor: Actor, now: Date): WaitingDue[] {
  if (row.register !== 'incidents') return [];
  const input = {
    awareAt: str(row, 'AwareAt'),
    decision: str(row, 'ReportabilityDecision'),
    ndisNoticeClass: str(row, 'NdisNoticeClass'),
    workSafeNotifiable: str(row, 'WorkSafeNotifiable'),
    notices: row.fields,
  };
  const out: WaitingDue[] = [];
  const add = (due: WaitingDue) => {
    if (!due.notice || !isBlank(row.fields[due.column])) return;
    if (!canDoRecordVerb('notification', row, actor, { notice: due.notice, columns: [due.column] }).ok) return;
    out.push(due);
  };
  const rules = noticeDeadlines(input);
  if (rules.length > 0) {
    for (const rule of rules) {
      if (!rule.notice || rule.conditional) continue;
      add({
        label: RULE_LABEL[rule.key] ?? 'Notice due',
        at: rule.dueAt,
        precision: 'instant',
        past: instantPast(rule.dueAt, now),
        column: rule.notice,
        notice: NOTICE_OF_COLUMN.get(rule.notice),
        obRow: rule.obRow,
      });
    }
  } else if (input.awareAt === null) {
    // Fail loud (D-13-27-6's spirit): a reportable call with no AwareAt still owes its notice. The rules are probed
    // for which notice; the due is the stored deadline, else none.
    const stored = str(row, 'NotificationDueAt');
    for (const rule of noticeDeadlines({ ...input, awareAt: PROBE_AWARE_AT })) {
      if (!rule.notice || !INITIAL_NOTICE_RULES.has(rule.key)) continue;
      add({
        label: RULE_LABEL[rule.key] ?? 'Notice due',
        at: stored,
        precision: 'instant',
        past: instantPast(stored, now),
        column: rule.notice,
        notice: NOTICE_OF_COLUMN.get(rule.notice),
        obRow: rule.obRow,
      });
    }
  }
  const final = finalReportNotice(textOf(row.fields.ReportabilityDecision));
  const dueOn = melbourneDayOf(str(row, 'FinalReportDueOn'));
  if (final && dueOn && textOf(row.fields.FinalReportRequired) === FINAL_REPORT_YES) {
    add({
      label: RULE_LABEL[final.key] ?? 'Final report due',
      at: dueOn,
      precision: 'day',
      past: dueOn < todayMelbourne(now),
      column: 'FinalReportGivenAt',
      notice: final.notice,
      obRow: OB_ROW_OF.get(final.key) ?? null,
    });
  }
  return out;
}

/**
 * 13.2 (D-13.2-P-5): the initial notice a reportable CM draft would start, as a provisional due, while the decision is
 * still Pending or blank. Read by `noticeDeadlines` with the draft in the decision's place (the one deadline engine,
 * never a second table). Each due is kept only where its notice column is blank and this actor may record that notice
 * (the notification gate, as `noticeDues` does). No `AwareAt` → none: the clock can't start, and the stored
 * `NotificationDueAt` line (the caller's) still shows. Display only: nothing here is written.
 */
function provisionalDues(row: RecordRow, actor: Actor, now: Date): WaitingDue[] {
  const draft = textOf(row.fields.ReportabilityDraft);
  const awareAt = str(row, 'AwareAt');
  if (!DRAFT_WITH_CLOCK.includes(draft) || awareAt === null) return [];
  const out: WaitingDue[] = [];
  for (const rule of noticeDeadlines({ decision: draft, awareAt, ndisNoticeClass: str(row, 'NdisNoticeClass') })) {
    if (!rule.notice || !INITIAL_NOTICE_RULES.has(rule.key) || !isBlank(row.fields[rule.notice])) continue;
    const notice = NOTICE_OF_COLUMN.get(rule.notice);
    if (!notice || !canDoRecordVerb('notification', row, actor, { notice, columns: [rule.notice] }).ok) continue;
    out.push({
      label: `If confirmed: ${RULE_LABEL[rule.key] ?? 'Notice due'}`,
      at: rule.dueAt,
      precision: 'instant',
      past: instantPast(rule.dueAt, now),
      column: rule.notice,
      notice,
      obRow: rule.obRow,
      provisional: true,
    });
  }
  return out;
}

/** The CM's draft view (D-13.2-P-5), or `null` when no draft is recorded (a row triaged before 13.2). */
function draftOf(row: RecordRow): ReportabilityDraftView | null {
  const value = textOf(row.fields.ReportabilityDraft);
  if (value === '') return null;
  return { value, note: str(row, 'ReportabilityDraftNote') };
}

/** The WHS "by" days due today or passed, with their done column blank (ISS-270; `whsDayDeadlines`, 13-28). */
function whsDayDues(row: RecordRow, today: string): WaitingDue[] {
  if (row.register !== 'incidents') return [];
  const rules = whsDayDeadlines({
    ClaimReceivedOn: str(row, 'ClaimReceivedOn'),
    MedicalCertificateReceivedOn: str(row, 'MedicalCertificateReceivedOn'),
    MentalInjuryClaim: str(row, 'MentalInjuryClaim'),
    RtwKnowledgeOn: str(row, 'RtwKnowledgeOn'),
  });
  const out: WaitingDue[] = [];
  for (const rule of rules) {
    if (!rule.notice || !isBlank(row.fields[rule.notice]) || rule.dueOn > today) continue;
    out.push({
      label: RULE_LABEL[rule.key] ?? 'Due',
      at: rule.dueOn,
      precision: 'day',
      past: rule.dueOn < today,
      column: rule.notice,
      obRow: rule.obRow,
    });
  }
  return out;
}

/** The handling due day of an open record, or null (a blank or unreadable day is never due). */
function handlingDue(row: RecordRow, today: string): WaitingDue | null {
  const spec = DUE_COLUMN[row.register];
  if (!spec) return null;
  const anchor = melbourneDayOf(textOf(row.fields[spec.column]) || null);
  if (anchor === null) return null;
  const day = spec.days ? addDaysTo(anchor, spec.days) : anchor;
  if (day > today) return null;
  return { label: spec.label, at: day, precision: 'day', past: day < today, column: spec.column };
}

// ---------------------------------------------------------------------------
// The candidates
// ---------------------------------------------------------------------------

interface Facts {
  row: RecordRow;
  status: string;
  today: string;
  now: Date;
  preparer: string | null;
  /** 13.2: an Incident whose report lacks a field triage can't add (`columnsToComplete`: triage would refuse it). */
  incomplete: boolean;
}

/** Waiting (with what is due, if anything, and the CM's draft on a decision), or `null`: not waiting on this actor. */
type Waiting = { dues?: WaitingDue[]; draft?: ReportabilityDraftView } | null;

/** Which kinds each role is offered (before the permission filter). */
type Candidate = (actor: Actor, facts: Facts) => Waiting;

const when = (yes: boolean): Waiting => (yes ? {} : null);

const CANDIDATES: Readonly<Record<WaitingKind, Candidate>> = {
  // 13.2 (D-13.2-P-3, -4): any Care Manager (D-13-L-15), on a New or Actioning Incident triage would refuse.
  'to-complete': (actor, { row, status, incomplete }) =>
    when(row.register === 'incidents' && (status === NEW || status === ACTIONING) && incomplete && holds(actor, 'CareManager')),
  // 13.2: not while the report is incomplete; triage would refuse it (`report-incomplete`), so it is to-complete.
  'to-triage': (actor, { row, status, incomplete }) =>
    when(
      status === NEW &&
        !incomplete &&
        ((CLIENT_FACING.has(row.register) && holds(actor, 'CareManager')) ||
          (!CLIENT_FACING.has(row.register) && isOversight(actor))),
    ),
  'handling-due': (actor, { row, today }) => {
    if (!holds(actor, 'CareManager')) return null;
    const due = handlingDue(row, today);
    return due ? { dues: [due] } : null;
  },
  // 13.2: the id is written by triage, which refuses an incomplete report; the record is to-complete until then.
  'client-id-to-add': (actor, { row, incomplete }) => when(holds(actor, 'CareManager') && !incomplete && needsClientId(row)),
  'to-close': (actor, { row, status }) =>
    when((row.register === 'complaints' || row.register === 'feedback') && status === EVALUATING && holds(actor, 'CareManager')),
  'to-approve': (actor, { row, status, preparer }) =>
    when(
      row.register === 'incidents' &&
        status === EVALUATING &&
        isOversight(actor) &&
        preparer !== null &&
        !sameUpn(preparer, upnOf(actor)),
    ),
  'preparer-unknown': (actor, { row, status, preparer }) =>
    when(row.register === 'incidents' && status === EVALUATING && isOversight(actor) && preparer === null),
  'reportability-pending': (actor, { row, now }) => {
    if (row.register !== 'incidents' || !(isOversight(actor) || isSahDecider(actor))) return null;
    const decision = textOf(row.fields.ReportabilityDecision);
    if (!(decision === '' || decision === PENDING)) return null;
    const dueAt = str(row, 'NotificationDueAt');
    const dues: WaitingDue[] =
      dueAt === null
        ? []
        : [{ label: 'Notification due', at: dueAt, precision: 'instant', past: instantPast(dueAt, now), column: 'NotificationDueAt' }];
    // 13.2 (D-13.2-P-5): the CM's draft, and what it would start if confirmed (provisional, after the stored line).
    dues.push(...provisionalDues(row, actor, now));
    const draft = draftOf(row);
    return draft ? { dues, draft } : { dues };
  },
  'notice-due': (actor, { row, now }) => {
    const dues = noticeDues(row, actor, now);
    return dues.length > 0 ? { dues } : null;
  },
  'whs-to-triage': (actor, { row }) =>
    when(
      row.register === 'incidents' &&
        holds(actor, 'HRWHS') &&
        isBlank(row.fields.WorkSafeNotifiable) &&
        (textOf(row.fields.WorkerInjured) === YES ||
          textOf(row.fields.ReportabilityDecision) === WORKSAFE ||
          // 13.2 (D-13.2-P-6): the CM asks HR & WHS in, or drafts it as WorkSafe.
          textOf(row.fields.HrWhsTriageNeeded) === HRWHS_ASKED ||
          textOf(row.fields.ReportabilityDraft) === DRAFT_WORKSAFE),
    ),
  'whs-day-due': (actor, { row, today }) => {
    if (!holds(actor, 'HRWHS')) return null;
    const dues = whsDayDues(row, today);
    return dues.length > 0 ? { dues } : null;
  },
  'hazard-open': (actor, { row }) =>
    when(row.register === 'risk' && holds(actor, 'HRWHS') && textOf(row.fields.RiskCategory) === WHS_RISK && isBlank(row.fields.HazardType)),
};

function preparerOf(row: RecordRow, ctx: WaitingContext): string | null {
  const map = ctx.preparedByUpn;
  if (!map || !Object.prototype.hasOwnProperty.call(map, row.ref)) return null;
  const upn = map[row.ref];
  return typeof upn === 'string' && upn.trim() !== '' ? upn : null;
}

/**
 * What is waiting on `actor`, as `{ kind, row, dues? }` items in input order, then `WAITING_KINDS` order. Completed
 * and Cancelled records are never waiting, and a blank due date is never due. "Today" is the Melbourne day of
 * `now`. Every item whose kind asks for a verb passes `canDoRecordVerb` for it (a `notice-due` item for each notice
 * it carries, checked as its dues are built), so the lens never offers what the gate refuses.
 */
export function recordsWaitingOn(
  actor: Actor,
  rows: readonly RecordRow[],
  now: Date,
  ctx: WaitingContext = {},
): WaitingItem[] {
  const today = todayMelbourne(now);
  const items: WaitingItem[] = [];
  for (const row of rows) {
    if (!KNOWN_REGISTERS.has(row.register)) continue;
    const status = textOf(row.fields.Status);
    if (status === COMPLETED || status === CANCELLED) continue;
    const preparer = preparerOf(row, ctx ?? {});
    const incomplete = columnsToComplete(row).length > 0;
    const facts: Facts = { row, status, today, now, preparer, incomplete };
    for (const kind of WAITING_KINDS) {
      const waiting = CANDIDATES[kind](actor, facts);
      if (waiting === null) continue;
      const verb = VERB_OF_WAITING[kind];
      if (verb !== null && kind !== 'notice-due' && !canDoRecordVerb(verb, row, actor, { preparedByUpn: preparer ?? undefined }).ok) {
        continue;
      }
      const item: WaitingItem = waiting.dues ? { kind, row, dues: waiting.dues } : { kind, row };
      items.push(waiting.draft ? { ...item, draft: waiting.draft } : item);
    }
  }
  return items;
}

// ---------------------------------------------------------------------------
// isMyStream (D-13-L-15)
// ---------------------------------------------------------------------------

/**
 * The actor is one of the Care Managers for the record's `BusinessUnit` (the C3 stream map, `STREAM_CARE_MANAGERS`).
 * False on a blank unit or a unit not in the map. A marker only: it is never consulted by `canDoRecordVerb`.
 */
export function isMyStream(actor: Actor, row: RecordRow): boolean {
  const unit = textOf(row.fields.BusinessUnit);
  const upn = upnOf(actor);
  if (unit === '' || upn.trim() === '') return false;
  const managers = Object.prototype.hasOwnProperty.call(STREAM_CARE_MANAGERS, unit) ? STREAM_CARE_MANAGERS[unit] : [];
  return (managers ?? []).some((cm) => sameUpn(cm, upn));
}
